import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { GraduationCap, LogOut, KeyRound } from "lucide-react";
import InstallPWA from "@/components/InstallPWA";
import DrawerHost from "@/components/drawers/DrawerHost";
import ThemeSwitcher from "@/components/shared/ThemeSwitcher";
import AppCacheControls from "@/components/shared/AppCacheControls";

export default function Shell({ title, subtitle, tabs, active, onTab, children }) {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const changePw = async () => {
    const current = window.prompt("Enter your CURRENT password:");
    if (!current) return;
    const next = window.prompt("Enter a NEW password (min 6 chars):");
    if (!next) return;
    try {
      await api.post("/auth/change-password", {current_password: current, new_password: next});
      toast.success("Password updated. Please sign in again.");
      await logout(); nav("/login");
    } catch(e){ toast.error(e?.response?.data?.detail || "Failed"); }
  };
  return (
    <div className="min-h-screen bg-[#FBF9F6]">
      <header className="border-b-2 border-black bg-white sticky top-0 z-20">
        <div className="max-w-7xl mx-auto px-3 sm:px-6 py-3 sm:py-4 flex items-center justify-between gap-2">
          <Link to="/" className="flex items-center gap-2 min-w-0">
            <div className="w-9 h-9 shrink-0 bg-[#C4B5FD] border-2 border-black rounded-lg grid place-items-center shadow-[2px_2px_0_0_#000]">
              <GraduationCap size={20} strokeWidth={2.5} />
            </div>
            <div className="min-w-0">
              <div className="display text-base sm:text-xl font-bold leading-none truncate">{title}</div>
              {subtitle && <div className="text-xs text-slate-600 truncate">{subtitle}</div>}
            </div>
          </Link>
          <div className="flex items-center gap-2 shrink-0">
            {user && (
              <div className="hidden lg:block text-right">
                <div className="font-bold text-sm">{user?.name}</div>
                <div className="text-xs text-slate-500 mono uppercase">{user?.role}</div>
              </div>
            )}
            <div className="block sm:block"><InstallPWA/></div>
            <ThemeSwitcher compact />
            <div className="relative group" data-testid="shell-app-menu">
              <button className="nb-btn nb-btn-ghost px-2 sm:px-3" data-testid="shell-app-menu-btn" title="App">⋯</button>
              <div className="absolute right-0 mt-1 z-50 hidden group-hover:block bg-white border-2 border-black rounded-xl shadow-lg">
                <AppCacheControls />
              </div>
            </div>
            {user && (
              <button data-testid="change-password-btn" onClick={changePw}
                      className="nb-btn nb-btn-ghost px-2 sm:px-3">
                <KeyRound size={16}/> <span className="hidden md:inline">Change PW</span>
              </button>
            )}
            {user && (
              <button data-testid="logout-btn" onClick={async ()=>{ await logout(); nav("/"); }}
                      className="nb-btn nb-btn-danger px-2 sm:px-3">
                <LogOut size={16}/> <span className="hidden sm:inline">Logout</span>
              </button>
            )}
          </div>
        </div>
        {tabs && (
          <div className="max-w-7xl mx-auto px-3 sm:px-6 pb-3 flex flex-nowrap overflow-x-auto gap-2 -mx-3 sm:mx-auto no-scrollbar">
            {tabs.map(t => (
              <button key={t.id}
                      data-testid={`tab-${t.id}`}
                      onClick={()=> t.disabled ? null : onTab(t.id)}
                      disabled={!!t.disabled}
                      title={t.title || ""}
                      className={`nb-btn shrink-0 text-xs sm:text-sm ${active===t.id ? "" : "nb-btn-ghost"} ${t.disabled ? "opacity-50 cursor-not-allowed line-through" : ""}`}>
                {t.label}{t.disabled && <span className="ml-1 text-[9px]">🔒</span>}
              </button>
            ))}
          </div>
        )}
      </header>
      <main className="max-w-7xl mx-auto p-3 sm:p-6">{children}</main>
      {user && (user.open_school || ["student", "educator"].includes(user.role)) && <DrawerHost />}
    </div>
  );
}
